import React, { useRef, useCallback } from 'react';
import {
  PRIMARY,
  TEXT_DARK,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  TEXT_MUTED,
} from '../../../components/legacy-ball/table-management/designTokens';

interface SubmissionPanelProps {
  submissions: { id: string; title: string }[];
  selectedSubmission: string | 'all';
  onSubmissionSelect: (submissionId: string | 'all') => void;
  loading: boolean;
  /** Set of submission IDs that have at least one review comment */
  submissionsWithComments?: Set<string>;
}

const PANEL_WIDTH = 220;

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: `${PANEL_WIDTH}px`,
    minWidth: `${PANEL_WIDTH}px`,
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    backgroundColor: BG_CARD,
    borderRight: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '8px 0',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 180px)',
    outline: 'none',
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '10px 16px',
    fontSize: '0.82rem',
    fontWeight: 500,
    color: TEXT_DARK,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    outline: 'none',
  },
  itemSelected: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '10px 16px',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: PRIMARY,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    borderRadius: '4px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    outline: 'none',
  },
  emptyText: {
    padding: '16px',
    fontSize: '0.82rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  skeletonItem: {
    height: '18px',
    margin: '10px 16px',
    borderRadius: '4px',
    backgroundColor: BG_SURFACE,
  },
};

function SkeletonLoader() {
  return (
    <>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} style={{ ...styles.skeletonItem, width: `${55 + i * 7}%` }} />
      ))}
    </>
  );
}

export function SubmissionPanel({
  submissions,
  selectedSubmission,
  onSubmissionSelect,
  loading,
  submissionsWithComments,
}: SubmissionPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Build full items list: ['all', ...submission ids]
  const allIds: (string | 'all')[] = ['all', ...submissions.map((s) => s.id)];

  const getCurrentIndex = useCallback(() => {
    if (selectedSubmission === 'all') return 0;
    const idx = submissions.findIndex((s) => s.id === selectedSubmission);
    return idx >= 0 ? idx + 1 : 0;
  }, [selectedSubmission, submissions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();

      const currentIdx = getCurrentIndex();
      let nextIdx: number;

      if (e.key === 'ArrowDown') {
        nextIdx = Math.min(currentIdx + 1, allIds.length - 1);
      } else {
        nextIdx = Math.max(currentIdx - 1, 0);
      }

      const nextItem = allIds[nextIdx];
      onSubmissionSelect(nextItem);

      // Scroll the selected button into view
      const buttons = containerRef.current?.querySelectorAll('[role="option"]');
      if (buttons && buttons[nextIdx]) {
        (buttons[nextIdx] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    },
    [getCurrentIndex, allIds, onSubmissionSelect],
  );

  if (loading) {
    return (
      <div style={styles.container} role="listbox" aria-label="Submission filter" aria-busy="true">
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={styles.container}
      role="listbox"
      aria-label="Submission filter"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <button
        role="option"
        style={selectedSubmission === 'all' ? styles.itemSelected : styles.item}
        onClick={() => onSubmissionSelect('all')}
        aria-selected={selectedSubmission === 'all'}
        tabIndex={-1}
      >
        All Submissions
      </button>
      {submissions.length === 0 && (
        <div style={styles.emptyText}>No submissions found.</div>
      )}
      {submissions.map((sub) => {
        const isSelected = selectedSubmission === sub.id;
        const hasComments = submissionsWithComments?.has(sub.id);
        return (
          <button
            key={sub.id}
            role="option"
            style={isSelected ? styles.itemSelected : styles.item}
            onClick={() => onSubmissionSelect(sub.id)}
            aria-selected={isSelected}
            title={sub.title}
            tabIndex={-1}
          >
            {hasComments && <span style={{ color: isSelected ? '#fff' : '#d32f2f', fontWeight: 700, marginRight: '4px' }}>»</span>}
            {sub.title}
          </button>
        );
      })}
    </div>
  );
}
